import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import { PaymentTransaction } from "../types/payment-transaction.entity";
import { useGetPaymentTransactionsQuery, useCountPaymentTransactionsQuery, PaymentTransactionsFilter } from "../payment-transactions.service";
import PaymentTransactionsTable from "../components/payment-transactions-table";
import { PaymentTransactionsTableSkeleton } from "../components/PaymentTransactionsTableSkeleton";
import { PaymentTransactionsSearch } from "../components/payment-transactions-search";
import { useCallback } from "react";
import { usePaginatedTableSearchParams } from "src/customHooks/usePaginatedTableSearchParams";

type PaymentTransactionsRouteState = {
  userId?: string;
  transactionId?: string;
  status?: string;
  service?: string;
};

const PaymentTransactionsPage = () => {
  const tableSearch = usePaginatedTableSearchParams<PaymentTransactionsRouteState>({
    parseState: (searchParams) => {
      const query: PaymentTransactionsRouteState = {};
      const userId = searchParams.get("userId");
      const transactionId = searchParams.get("transactionId");
      const status = searchParams.get("status");
      const service = searchParams.get("service");

      if (userId) query.userId = userId;
      if (transactionId) query.transactionId = transactionId;
      if (status) query.status = status;
      if (service) query.service = service;

      return query;
    },
    serializeState: (state) => {
      const nextParams = new URLSearchParams();
      if (state.userId) nextParams.set("userId", state.userId);
      if (state.transactionId) nextParams.set("transactionId", state.transactionId);
      if (state.status) nextParams.set("status", state.status);
      if (state.service) nextParams.set("service", state.service);
      return nextParams;
    },
  });

  const initialRouteState = tableSearch.state;
  const initialQuery: PaymentTransactionsFilter = {
    ...(initialRouteState.userId ? { userId: initialRouteState.userId } : {}),
    ...(initialRouteState.transactionId ? { transactionId: initialRouteState.transactionId } : {}),
    ...(initialRouteState.status ? { paymentStatus: initialRouteState.status as PaymentTransactionsFilter["paymentStatus"] } : {}),
    ...(initialRouteState.service ? { PaymentService: initialRouteState.service as PaymentTransactionsFilter["PaymentService"] } : {}),
  };

  const { data, numberOfPages, currentPage, next, prev, isSuccess, isError, isLoading, isFetching } =
    usePagination<PaymentTransaction & { id: string }>({
      useGetDataQuery: useGetPaymentTransactionsQuery,
      useCountData: useCountPaymentTransactionsQuery,
      initQuery: initialQuery,
      initPageTokens: tableSearch.pageTokens,
      initCurrentIndex: tableSearch.currentIndex,
      syncKey: tableSearch.syncKey,
      onPaginationStateChange: tableSearch.replacePagination,
    });

  const updateQuery = useCallback((newQuery: PaymentTransactionsFilter) => {
    tableSearch.replaceFilters({
      userId: newQuery.userId,
      transactionId: newQuery.transactionId,
      status: newQuery.paymentStatus,
      service: newQuery.PaymentService,
    });
  }, [tableSearch]);

  const showTableSkeleton = !data && (isLoading || isFetching);

  return (
    <div className="page_container">
      <PageHeader text="Payment Transactions" />

      <div className="mb-4">
        <PaymentTransactionsSearch updateQuery={updateQuery} initialQuery={initialQuery} />
      </div>

      {isSuccess ? (
        <>
          {showTableSkeleton ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
                <thead className="bg-gray-900 text-white text-center">
                  <tr>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Transaction ID</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">User</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Status</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Service</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Payment Amount</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Wallet Amount</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Created</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <PaymentTransactionsTableSkeleton rows={10} />
                </tbody>
              </table>
            </div>
          ) : (
            <PaymentTransactionsTable data={data ?? []} />
          )}
          <Pagination numberOfPages={numberOfPages} currentPage={currentPage} prev={prev} next={next} isLoading={isLoading || isFetching} />
        </>
      ) : isError ? (
        <div className="mt-6 p-4 rounded border border-red-200 bg-red-50 text-red-700">
          Something went wrong loading payment transactions. Please adjust your filters or try again.
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

export default PaymentTransactionsPage;
